/**
 * Learning Moments → Error Categories
 *
 * The candidate error taxonomy mined from the prod analysis corpus, with the
 * questions that actually provoked each category. Read-only exploration — this
 * is how we decide which categories deserve to become authored moments.
 *
 * Method, evidence and caveats: docs/pedagogy/insights-candidate-catalogue.md
 */

import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { fetchWithAdmin } from '../../api/fetchHelper';
import { LearningMomentTabs } from './LearningMomentTabs';
import './InsightCategories.css';

interface CategorySummary {
  key: string;
  label: string;
  description: string;
  minedShare: { prod: number; cisce: number };
  stepCount: number;
  questionCount: number;
  studentCount: number;
}

interface SummaryResponse {
  scannedRuns: number;
  scannedSteps: number;
  wrongSteps: number;
  unmatchedSteps: number;
  scannedAt: string;
  categories: CategorySummary[];
}

interface CategoryHit {
  modelOutputId: string;
  submissionId?: string;
  userId?: string;
  stepNumber?: number;
  feedback: string;
  content?: string;
}

interface CategoryQuestion {
  questionId: string;
  text: string;
  subject: string;
  topic: string;
  attemptCount: number;
  studentCount: number;
  hits: CategoryHit[];
}

interface QuestionsResponse {
  key: string;
  label: string;
  description: string;
  questions: CategoryQuestion[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 25;

export function InsightCategories() {
  const [selected, setSelected] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);
  // `question.subject` is the internal key ('maths'); never render it raw.
  const subjectLabel = useSubjectLabel();

  const summary = useQuery({
    queryKey: ['insight-categories'],
    queryFn: () => fetchWithAdmin<SummaryResponse>('/console/insight-categories'),
  });

  const questions = useQuery({
    queryKey: ['insight-category-questions', selected, page],
    queryFn: () => fetchWithAdmin<QuestionsResponse>(
      `/console/insight-categories/${selected}/questions?page=${page}&pageSize=${PAGE_SIZE}`,
    ),
    enabled: !!selected,
  });

  const selectCategory = (key: string) => {
    setSelected(key === selected ? null : key);
    setPage(1);
    setExpanded(null);
  };

  const totalPages = questions.data ? Math.ceil(questions.data.total / questions.data.pageSize) : 0;

  return (
    <div className="lm-page">
      <div className="lm-header">
        <h1>Learning Moments</h1>
      </div>
      <LearningMomentTabs active="categories" />

      <p className="ic-intro">
        Candidate error categories mined from stored solution analyses, with the questions that
        provoked each. Matching is keyword-based over the analyser&rsquo;s own per-step feedback —
        indicative, not a measurement. Counts also over-weight errors early in long solutions,
        because one root mistake cascades into several flagged steps.
      </p>

      {summary.isLoading && <p>Scanning analyses…</p>}
      {summary.isError && <p className="ic-error">Failed to load: {(summary.error as Error).message}</p>}

      {summary.data && (
        <>
          <div className="ic-stats">
            <span><strong>{summary.data.scannedRuns.toLocaleString()}</strong> analyses</span>
            <span><strong>{summary.data.scannedSteps.toLocaleString()}</strong> steps</span>
            <span><strong>{summary.data.wrongSteps.toLocaleString()}</strong> wrong steps</span>
            <span
              title="Wrong steps matching no category — where the taxonomy is incomplete"
            >
              <strong>{summary.data.unmatchedSteps.toLocaleString()}</strong> unmatched
            </span>
          </div>

          <table className="dt ic-categories">
            <thead>
              <tr>
                <th>Category</th>
                <th className="ic-num">Steps</th>
                <th className="ic-num">Questions</th>
                <th className="ic-num">Students</th>
                <th className="ic-num" title="Share of the mined prod corpus at analysis time">Prod %</th>
                <th className="ic-num" title="Share of the CISCE examiner corpus — a large gap means our analyser under-reports this class">Examiners %</th>
              </tr>
            </thead>
            <tbody>
              {summary.data.categories.map(c => {
                const gap = c.minedShare.cisce >= 3 * Math.max(c.minedShare.prod, 0.1);
                return (
                  <tr
                    key={c.key}
                    className={`ic-row ${selected === c.key ? 'ic-row-active' : ''}`}
                    onClick={() => selectCategory(c.key)}
                  >
                    <td>
                      <div className="ic-label">{c.label}</div>
                      <div className="ic-desc">{c.description}</div>
                    </td>
                    <td className="ic-num">{c.stepCount.toLocaleString()}</td>
                    <td className="ic-num">{c.questionCount.toLocaleString()}</td>
                    <td className="ic-num">{c.studentCount.toLocaleString()}</td>
                    <td className="ic-num">{c.minedShare.prod.toFixed(1)}%</td>
                    <td className={`ic-num ${gap ? 'ic-gap' : ''}`} title={gap ? 'Examiners report this far more often than our analyser does' : undefined}>
                      {c.minedShare.cisce.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {selected && (
        <div className="ic-questions">
          <h2>{questions.data?.label ?? 'Questions'}</h2>
          {questions.isLoading && <p>Loading questions…</p>}
          {questions.data && questions.data.questions.length === 0 && (
            <p className="lm-empty">No questions matched this category.</p>
          )}
          {questions.data && questions.data.questions.length > 0 && (
            <>
              <table className="dt">
                <thead>
                  <tr>
                    <th>Question</th>
                    <th>Subject</th>
                    <th>Topic</th>
                    <th className="ic-num">Attempts</th>
                    <th className="ic-num">Students</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.data.questions.map(q => (
                    <Fragment key={q.questionId}>
                      <tr
                        className="ic-row"
                        onClick={() => setExpanded(expanded === q.questionId ? null : q.questionId)}
                      >
                        <td className="ic-qtext">{q.text}</td>
                        <td>{subjectLabel(q.subject)}</td>
                        <td>{q.topic}</td>
                        <td className="ic-num">{q.attemptCount}</td>
                        <td className="ic-num">{q.studentCount}</td>
                      </tr>
                      {expanded === q.questionId && (
                        <tr className="ic-hits-row">
                          <td colSpan={5}>
                            <ul className="ic-hits">
                              {q.hits.map((h, i) => (
                                <li key={`${h.modelOutputId}-${h.stepNumber}-${i}`}>
                                  <span className="ic-step">
                                    {h.stepNumber != null ? `Step ${h.stepNumber}` : 'Step'}
                                  </span>
                                  {h.content && <code className="ic-content">{h.content}</code>}
                                  <span className="ic-feedback">{h.feedback}</span>
                                  {h.submissionId && (
                                    <Link to={`/submissions/${h.submissionId}`} className="ic-link">
                                      view attempt →
                                    </Link>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div className="ic-pager">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                  <span>Page {page} of {totalPages} · {questions.data.total} questions</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
